import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { ImagePreloader } from '../components/ImagePreloader'
import { LexicalHtmlRenderer } from '../components/Portfolio/components/LexicalHtmlRenderer'
import PlayButton from '../components/Portfolio/components/PlayButton'
import { StreamingLinks } from '../components/Portfolio/components/StreamingLinks'
import { ROLE_SLUGS, roleSlugToLabel } from '../data/roles'
import type { Album } from '../types/portfolio'

interface LoaderData {
  item: Album
}

export default function PostPage() {
  const { item } = useLoaderData<LoaderData>()
  const navigate = useNavigate()

  const roleSlugs: string[] = []
  for (const slug of ROLE_SLUGS) {
    if (item[slug]) roleSlugs.push(slug)
  }

  const roles = roleSlugs.map((slug) => roleSlugToLabel(slug))

  const className = 'w-full rounded-lg object-cover'

  return (
    <>
      <div
        className="flex items-start justify-between gap-4"
        data-aos="fade-in"
      >
        <div>
          <h3 className="text-xl font-semibold">{item.title}</h3>
          <p className="text-sm mt-1 text-neutral-500 dark:text-neutral-400">
            {item.artist} · {item.releaseYear} ·{' '}
            {item.genre?.flatMap((g: { genre: string }) => g.genre).join(', ')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void navigate('/', { replace: true })
          }}
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-neutral-300 text-neutral-500 transition-colors hover:border-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:border-neutral-600 dark:text-neutral-400 dark:hover:border-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
          aria-label="Close details"
        >
          <FontAwesomeIcon icon={faXmark} className="text-sm" />
        </button>
      </div>
      <div
        className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2"
        data-aos="fade-in"
      >
        <div className="relative">
          <ImagePreloader
            src={item.image}
            alt={item.title}
            className={className}
            loading="lazy"
            width={365}
            height={365}
          />
          {item.songs && item.songs.length > 0 && (
            <div className="pointer-events-none relative inset-0 flex items-center justify-center">
              <div className="pointer-events-auto">
                <PlayButton albumId={item.id} />
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col justify-end">
          {item.songs && item.songs.length > 0 && (
            <div className="flex-row">
              <h4 className="text-base pb-1 pt-4 font-semibold text-neutral-800 dark:text-neutral-200">
                Tracks
              </h4>
              <ul className="list-inside list-decimal">
                {item.songs.map((song) => (
                  <li
                    key={song.id}
                    className="text-sm text-neutral-600 dark:text-neutral-400"
                  >
                    {song.songTitle}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.description && (
            <div className="flex-row">
              <h4 className="text-base pb-1 pt-4 font-semibold text-neutral-800 dark:text-neutral-200">
                Description
              </h4>
              <div className="text-sm h-full max-h-64 min-h-24 overflow-y-auto whitespace-pre-wrap leading-relaxed text-neutral-600 dark:text-neutral-400">
                <LexicalHtmlRenderer description={item.description} />
              </div>
            </div>
          )}
          <div className="flex-row">
            {item.links.length > 0 && (
              <div className="mt-4">
                <StreamingLinks links={item.links} />
              </div>
            )}
            {roles.length > 0 && (
              <div className="flex-row">
                {/* <h4 className="pt-2 pb-1 text-lg font-semibold">
                                    Role
                                </h4> */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <span
                      key={role}
                      className="text-xs rounded-full border border-neutral-200 bg-neutral-100 px-2.5 py-1 font-medium text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
